import { Link } from 'react-router-dom';
import {
  ArrowRight, ShieldCheck, LineChart, Star, ClipboardList,
  FileCheck, Lock, ScrollText,
} from 'lucide-react';

const CONTAINER = 'max-w-5xl mx-auto px-6 lg:px-8';
const H1 = 'text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-[#0B132B]';
const H2 = 'text-2xl sm:text-3xl font-semibold tracking-tight text-[#0B132B]';
const EYEBROW = 'text-[11px] font-bold uppercase tracking-[0.22em] text-[#DA291C]';
const BODY = 'text-base sm:text-lg text-slate-500 leading-relaxed';

const PILLARS = [
  {
    icon: ShieldCheck,
    title: 'Worker verification',
    body: 'Every worker submits a Smart ID which is confirmed by their manager through an HR verification process before they can apply for shifts.',
  },
  {
    icon: LineChart,
    title: 'Reliability history',
    body: 'A live reliability score is calculated from previous shifts — no-shows, late cancellations and completed shifts. Fully transparent to every manager.',
  },
  {
    icon: Star,
    title: 'Ratings',
    body: 'Both sides rate each shift after completion. Ratings compound over time and are visible on the worker profile.',
  },
  {
    icon: ClipboardList,
    title: 'Accountability',
    body: 'Workers submit a periodic till check every three hours during active shifts, plus a final till log at the end. Discrepancies are automatically flagged.',
  },
  {
    icon: FileCheck,
    title: 'Audit trail',
    body: 'Every event — application, assignment, till check, sign-off — is time-stamped and immutable. Ready for HR, payroll or compliance review.',
  },
  {
    icon: ScrollText,
    title: 'UK GDPR',
    body: 'UK-based data handling with versioned consent audit trails, right-to-erasure and data export. Marketing communications are opt-in only.',
  },
  {
    icon: Lock,
    title: 'Security',
    body: 'Role-based access controls, encrypted-at-rest storage, TLS in transit, rate-limited authentication and detailed system audit logs.',
  },
];

export default function TrustCentrePage() {
  return (
    <div data-testid="trust-centre-page">
      <section className={`${CONTAINER} pt-32 pb-16 lg:pt-40 lg:pb-20`}>
        <div className="max-w-3xl">
          <p className={EYEBROW}>Trust Centre</p>
          <h1 className={`${H1} mt-5`}>Trust, by design.</h1>
          <p className={`${BODY} mt-6 max-w-xl`}>
            Seven commitments — the reason customer-facing businesses can rely on ShiftCover365 for the moments that matter.
          </p>
        </div>
      </section>

      <section className={`${CONTAINER} pb-24 lg:pb-32`}>
        <div className="space-y-6">
          {PILLARS.map((p, i) => (
            <div key={p.title} className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-start rounded-3xl bg-white border border-slate-200/70 p-8 sm:p-10 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(11,19,43,0.06)] transition-[transform,box-shadow] duration-300" data-testid={`trust-pillar-${p.title.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="flex items-center gap-5 md:block">
                <p className="text-5xl md:text-6xl font-bold text-slate-200 tabular-nums leading-none">
                  0{i + 1}
                </p>
                <div className="h-11 w-11 rounded-2xl bg-[#0B132B] flex items-center justify-center md:mt-6">
                  <p.icon className="h-5 w-5 text-white" strokeWidth={1.8} />
                </div>
              </div>
              <div>
                <h2 className={`${H2}`}>{p.title}</h2>
                <p className="mt-3 text-[15px] text-slate-500 leading-relaxed max-w-2xl">{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50/70 py-20 lg:py-24">
        <div className={`${CONTAINER} text-center max-w-2xl`}>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#0B132B]">
            Trust you can verify.
          </h2>
          <p className={`${BODY} mt-4`}>Questions? Email <a href="mailto:support@shiftcover365.co.uk" className="underline text-[#0B132B] font-semibold">support@shiftcover365.co.uk</a>.</p>
          <Link to="/register?role=manager" className="mt-8 inline-flex items-center gap-2 h-14 px-8 rounded-full bg-[#DA291C] text-white font-semibold hover:bg-[#b91f13] transition-colors" data-testid="trust-cta">
            Start Free Trial <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
